// ============================================
// PAYMENT MODAL COMPONENT
// Payment methods UI with Stripe integration
// ============================================

import React, { useState } from 'react';
import { 
  X, 
  CreditCard, 
  Banknote, 
  Smartphone,
  QrCode,
  Receipt,
  CheckCircle,
  Printer,
  Mail,
  Split,
  Percent,
  Calculator,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { paymentService } from '@/services/api';

const PAYMENT_METHODS = [
  { 
    id: 'stripe', 
    name: 'Card (Stripe)', 
    icon: CreditCard, 
    description: 'Visa, MC, Apple Pay',
    color: 'bg-indigo-500',
    available: true,
    isStripe: true,
  },
  { 
    id: 'card', 
    name: 'Card Terminal', 
    icon: CreditCard, 
    description: 'In-store terminal',
    color: 'bg-blue-500',
    available: true,
  },
  { 
    id: 'cash', 
    name: 'Cash', 
    icon: Banknote, 
    description: 'NOK / SEK',
    color: 'bg-green-500',
    available: true,
  },
  { 
    id: 'vipps', 
    name: 'Vipps', 
    icon: Smartphone, 
    description: 'Mobile payment',
    color: 'bg-orange-500',
    available: true,
  },
  { 
    id: 'online', 
    name: 'Online', 
    icon: QrCode, 
    description: 'Already paid online',
    color: 'bg-purple-500',
    available: true,
  },
];

const QUICK_CASH_AMOUNTS = [100, 200, 500, 1000];

export function PaymentModal({
  order,
  totals,
  onPayment,
  onClose,
  formatPrice,
  vatLabel,
  isManager,
  outletId,
}) {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [cashReceived, setCashReceived] = useState('');
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [stripeError, setStripeError] = useState(null);

  const totalAfterDiscount = Math.max(0, totals.total - discount);
  const changeAmount = cashReceived ? Math.max(0, parseFloat(cashReceived) - totalAfterDiscount) : 0;

  // Handle Stripe payment - creates checkout session and redirects
  const handleStripePayment = async () => {
    setProcessing(true);
    setStripeError(null);
    
    try {
      // Need a backend order ID - check if order already has one
      const orderId = order?.id;
      
      if (!orderId || orderId.startsWith('ORD-') || orderId.startsWith('LOCAL-')) {
        // Order not yet saved to backend - inform user
        setStripeError('Please save order first before processing card payment');
        setProcessing(false);
        return;
      }
      
      // Create Stripe Checkout session
      const checkoutData = await paymentService.createCheckoutSession(
        orderId,
        outletId,
        'NOK'
      );
      
      if (checkoutData.url) {
        // Open Stripe Checkout in new tab (so POS stays open)
        window.open(checkoutData.url, '_blank');
        
        // Show success message
        setCompleted(true);
        setTimeout(() => {
          onPayment('stripe');
        }, 2000);
      }
    } catch (err) {
      console.error('Stripe checkout error:', err);
      setStripeError(err.response?.data?.detail || err.message || 'Failed to create payment session');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = () => {
    // Check if Stripe was selected
    const method = PAYMENT_METHODS.find(m => m.id === selectedMethod);
    if (method?.isStripe) {
      handleStripePayment();
      return;
    }
    
    setProcessing(true);
    
    // Simulate payment processing for non-Stripe methods
    setTimeout(() => {
      setProcessing(false);
      setCompleted(true);
      
      // Close after showing success
      setTimeout(() => {
        onPayment(selectedMethod);
      }, 1500);
    }, 1000);
  };

  const handleQuickCash = (amount) => {
    setCashReceived(amount.toString());
  };

  // Success state
  if (completed) {
    const isStripePayment = selectedMethod === 'stripe';
    
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <div className="text-center py-8">
            <div className={cn(
              "w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4",
              isStripePayment ? "bg-indigo-100" : "bg-green-100"
            )}>
              {isStripePayment ? (
                <ExternalLink className="w-10 h-10 text-indigo-600" />
              ) : (
                <CheckCircle className="w-10 h-10 text-green-600" />
              )}
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {isStripePayment ? 'Stripe Checkout Opened!' : 'Payment Complete!'}
            </h2>
            <p className="text-gray-500 mb-6">
              {isStripePayment 
                ? `${formatPrice(totalAfterDiscount)} - Customer can complete payment in new tab`
                : `${formatPrice(totalAfterDiscount)} paid via ${selectedMethod}`
              }
            </p>
            {isStripePayment && (
              <p className="text-sm text-gray-400 mb-4">
                The order will be marked as paid once the customer completes checkout
              </p>
            )}
            <div className="flex justify-center gap-3">
              <Button variant="outline" className="gap-2">
                <Printer className="w-4 h-4" />
                Print Receipt
              </Button>
              <Button variant="outline" className="gap-2">
                <Mail className="w-4 h-4" />
                Email Receipt
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl">Payment</DialogTitle>
            <Badge variant="outline" className="font-mono text-base">
              Order #{order?.id?.split('-').pop() || '---'}
            </Badge>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6">
          {/* Left Column - Order Summary */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-700 mb-3">Order Summary</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 max-h-48 overflow-auto">
                {order?.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.quantity}× {item.name}
                    </span>
                    <span className="font-mono">
                      {formatPrice(item.price * item.quantity)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-mono">{formatPrice(totals.subtotal)}</span>
              </div>
              
              {Object.entries(totals.vatBreakdown || {}).map(([rate, values]) => (
                <div key={rate} className="flex justify-between text-sm text-gray-500">
                  <span>{vatLabel} {rate}</span>
                  <span className="font-mono">{formatPrice(values.vat)}</span>
                </div>
              ))}

              {discount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount</span>
                  <span className="font-mono">-{formatPrice(discount)}</span>
                </div>
              )}

              <Separator />

              <div className="flex justify-between text-xl font-bold">
                <span>Total</span>
                <span className="font-mono">{formatPrice(totalAfterDiscount)}</span>
              </div>
            </div>

            {/* Manager Actions */}
            {isManager && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1 gap-2">
                  <Percent className="w-4 h-4" />
                  Add Discount
                </Button>
                <Button variant="outline" size="sm" className="flex-1 gap-2">
                  <Split className="w-4 h-4" />
                  Split Bill
                </Button>
              </div>
            )}
          </div>

          {/* Right Column - Payment Methods */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-700 mb-3">Payment Method</h3>
            
            {/* Payment Method Buttons */}
            <div className="grid grid-cols-2 gap-3">
              {PAYMENT_METHODS.map(method => {
                const Icon = method.icon;
                return (
                  <button
                    key={method.id}
                    data-testid={`payment-method-${method.id}`}
                    onClick={() => {
                      setSelectedMethod(method.id);
                      if (method.id === 'cash') {
                        setCashReceived('');
                      }
                    }}
                    disabled={!method.available}
                    className={cn(
                      'flex flex-col items-center p-4 rounded-xl border-2 transition-all',
                      'hover:shadow-md hover:-translate-y-0.5',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      selectedMethod === method.id
                        ? 'border-brand-orange bg-brand-orange/5 shadow-md'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    )}
                  >
                    <div className={cn(
                      'w-12 h-12 rounded-full flex items-center justify-center mb-2',
                      method.color
                    )}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="font-medium text-gray-800">{method.name}</span>
                    <span className="text-xs text-gray-500">{method.description}</span>
                  </button>
                );
              })}
            </div>

            {/* Cash Input (when cash selected) */}
            {selectedMethod === 'cash' && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <Label>Cash Received</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">kr</span>
                  <Input
                    data-testid="cash-input"
                    type="number"
                    value={cashReceived}
                    onChange={(e) => setCashReceived(e.target.value)}
                    className="pl-9 font-mono text-lg"
                    placeholder="0"
                    autoFocus
                  />
                </div>
                
                {/* Quick amounts */}
                <div className="flex gap-2">
                  {QUICK_CASH_AMOUNTS.map(amount => (
                    <Button
                      key={amount}
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuickCash(amount)}
                      className="flex-1 font-mono"
                    >
                      {amount}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickCash(totalAfterDiscount)}
                    className="flex-1"
                  >
                    Exact
                  </Button>
                </div>

                {/* Change calculation */}
                {parseFloat(cashReceived) >= totalAfterDiscount && (
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <span className="text-green-700 font-medium">Change</span>
                    <span className="font-mono font-bold text-lg text-green-700">
                      {formatPrice(changeAmount)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Stripe Info (when Stripe selected) */}
            {selectedMethod === 'stripe' && (
              <div className="bg-indigo-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-indigo-700">
                  <ExternalLink className="w-4 h-4" />
                  <span className="font-medium">Stripe Checkout</span>
                </div>
                <p className="text-sm text-indigo-600">
                  Opens secure Stripe payment page in a new tab. Customer can pay with card, Apple Pay, or Google Pay.
                </p>
                {!order?.id || order?.id?.startsWith('ORD-') || order?.id?.startsWith('LOCAL-') ? (
                  <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                    ⚠️ Order must be saved before processing Stripe payment. Click &quot;Send to Kitchen&quot; first.
                  </p>
                ) : null}
              </div>
            )}

            {/* Error Display */}
            {stripeError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{stripeError}</p>
              </div>
            )}

            {/* Complete Payment Button */}
            <Button
              data-testid="btn-complete-payment"
              className="w-full h-14 text-lg gap-2 bg-brand-orange hover:bg-brand-orange/90"
              disabled={
                !selectedMethod || 
                processing ||
                (selectedMethod === 'cash' && parseFloat(cashReceived || 0) < totalAfterDiscount)
              }
              onClick={handlePayment}
            >
              {processing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Complete Payment - {formatPrice(totalAfterDiscount)}
                </>
              )}
            </Button>

            {/* Receipt Options */}
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" defaultChecked className="rounded" />
                <span>Print receipt</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="rounded" />
                <span>Email receipt</span>
              </label>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
